import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Combobox,
  Icon,
  MenuStatus,
  Note,
  type ComboboxOption,
  type MenuStatusTone,
} from '@repo/design-system';
import { semantic, space, type StatusRole, typeRole } from '@repo/design-tokens';
import {
  CAMERA_WALL_FILTER_LABELS,
  CAMERA_WALL_STATE_ROLE,
  CameraPicture,
  type CameraWallFeed,
  type CameraWallState,
} from '@repo/ui';
import type { RTSPEndpoint } from '@repo/frontend-types';

import { pickerStreams } from './streamLinkRules';
import { streamDisplayName } from './streamDisplayName';
import type { CameraPanelStreamsStatus } from './CameraPanel';

/**
 * The menu dot each health role draws: the role's own mark (`MenuStatus`'s `lavender` is
 * `support.lavender`, `errorStrong` is `semanticColor.errorStrong`). Only the roles the wall gives a
 * feed's health appear (`CAMERA_WALL_STATE_ROLE`).
 */
const MENU_TONE_OF_ROLE = {
  information: 'lavender',
  deadEnd: 'errorStrong',
} as const satisfies Partial<Record<StatusRole, MenuStatusTone>>;

/** A wall state's dot. A role the wall gives with no entry above fails the type check, not the dot. */
const toneOf = (state: CameraWallState): MenuStatusTone =>
  MENU_TONE_OF_ROLE[CAMERA_WALL_STATE_ROLE[state]];

/**
 * Card 04's health trailing: the wall's own state word, on the dot of the role the camera wall gives
 * that state (epic onboarding-status-colours), so the picker and the wall cannot disagree.
 */
export const STATUS_TONE: Record<CameraWallState, MenuStatusTone> = {
  healthy: toneOf('healthy'),
  warning: toneOf('warning'),
  error: toneOf('error'),
  connecting: toneOf('connecting'),
  not_configured: toneOf('not_configured'),
};

export interface CameraStreamSectionProps {
  refid: string;
  /** The camera's name as the panel shows it. */
  displayName: string;
  /** The camera's stream id, or null for a camera with no stream. */
  endpointId: number | null;
  /** What the panel calls the camera's stream (the read's name, else what the camera caches). */
  streamLabel: string | null;
  streams?: readonly RTSPEndpoint[];
  streamsStatus: CameraPanelStreamsStatus;
  /** The wall's feeds: each option's picture and health word are the wall's own. */
  feeds: readonly CameraWallFeed[];
  /** Every stream a camera holds, on ANY floor — the picker offers only the rest. */
  heldStreamIds: ReadonlySet<number>;
  /** The wall's order (`compareCameraWallFeeds`), so the picker lists streams as the wall does. */
  streamOrder?: (a: RTSPEndpoint, b: RTSPEndpoint) => number;
  onLink: (refid: string, endpointId: number) => void;
  onUnlink: (refid: string) => void;
}

/**
 * The docked panel's Stream section (`camera-feeds-page-link`, frames 06 and 07).
 *
 * A linked camera shows the stream with **Change** and **Unlink**; a camera with no stream — and a
 * linked one after Change — shows the stream picker: only the streams no camera holds, each with
 * card 21's Thumb picture and the wall's health word (card 04's *Item · count and picture*). Picking
 * links; the host decides what that writes (one write, one draft save).
 *
 * Focus follows the operator, because the control that held it disappears on every action here:
 * Change moves it into the picker, Escape out of Change returns it to Change, a pick lands it on
 * Change, and Unlink moves it into the picker so the next stream can be linked at once.
 */
const CameraStreamSection: React.FC<CameraStreamSectionProps> = ({
  refid,
  displayName,
  endpointId,
  streamLabel,
  streams,
  streamsStatus,
  feeds,
  heldStreamIds,
  streamOrder,
  onLink,
  onUnlink,
}) => {
  const pickerId = useId();
  const changeRef = useRef<HTMLButtonElement>(null);
  /*
   * The Change in progress, keyed by the camera AND the stream it was opened on — derived rather
   * than reset in an effect, so a pick (the stream changes) or a re-target ends it in the SAME render
   * that draws the row. An effect-based reset left one stale render with the picker still drawn,
   * and the focus meant for Change was spent before Change existed (review).
   */
  const [changingFor, setChangingFor] = useState<string | null>(null);
  const changeKey = `${refid}:${endpointId ?? ''}`;
  const changing = changingFor === changeKey;
  /** Where focus goes once a render has drawn it — the element that held it is gone. */
  const pendingFocus = useRef<'picker' | 'change' | null>(null);

  const hasStream = endpointId != null;
  const showPicker = !hasStream || changing;

  useEffect(() => {
    const want = pendingFocus.current;
    if (!want) return;
    /*
     * The picker, or — when the stream read has failed and no picker is drawn — the sentence saying
     * so, so an Unlink there does not drop focus out of the panel (Copilot, PR #2004).
     */
    const target =
      want === 'picker'
        ? (document.getElementById(pickerId) ?? document.getElementById(`${pickerId}-failed`))
        : changeRef.current;
    // Spent only once its target exists: a render that has not drawn it yet keeps the request.
    if (!target) return;
    pendingFocus.current = null;
    target.focus();
  });

  /*
   * The streams the picker offers, with their pictures and health words — rebuilt only when what
   * they show changes, not on every Name keystroke the panel re-renders for (review).
   */
  const options = useMemo<ComboboxOption[]>(() => {
    const feedById = new Map(feeds.map(f => [Number(f.id), f]));
    return pickerStreams({
      streams: streams ?? [],
      heldIds: heldStreamIds,
      exclude: endpointId,
      order: streamOrder,
    }).map(stream => {
      const name = streamDisplayName(stream);
      const feed: CameraWallFeed = feedById.get(stream.id) ?? {
        id: String(stream.id),
        name,
        state: 'connecting',
      };
      const word = CAMERA_WALL_FILTER_LABELS[feed.state];
      return {
        value: String(stream.id),
        label: name,
        group: 'Not placed',
        picture: <CameraPicture feed={feed} size="thumb" />,
        trailing: <MenuStatus tone={STATUS_TONE[feed.state]}>{word}</MenuStatus>,
        trailingText: word,
      };
    });
  }, [feeds, streams, heldStreamIds, endpointId, streamOrder]);

  if (!showPicker) {
    return (
      <Section>
        <span className="label">Stream</span>
        <Row>
          <span className="value">{streamLabel}</span>
          <Button
            ref={changeRef}
            variant="frosted"
            size="sm"
            iconLeft={<Icon name="arrow-right-arrow-left" />}
            // Opens with the visible word, so the two agree (WCAG 2.5.3).
            aria-label={`Change stream for ${displayName}`}
            disabled={streamsStatus === 'failed'}
            onClick={() => {
              pendingFocus.current = 'picker';
              setChangingFor(changeKey);
            }}
          >
            Change
          </Button>
          <Button
            variant="text"
            size="sm"
            aria-label={`Unlink ${streamLabel} from ${displayName}`}
            onClick={() => {
              pendingFocus.current = 'picker';
              onUnlink(refid);
            }}
          >
            Unlink
          </Button>
        </Row>
      </Section>
    );
  }

  if (streamsStatus === 'failed') {
    return (
      <Section>
        <span className="label">Stream</span>
        <div id={`${pickerId}-failed`} tabIndex={-1}>
          <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
            Streams couldn’t be read, so none can be offered. The wall above says why.
          </Note>
        </div>
      </Section>
    );
  }

  const loading = streamsStatus === 'loading';
  const noStreams = !loading && (streams?.length ?? 0) === 0;
  /** What the open list says with no rows: none to offer at all, or none matching what was typed. */
  const emptyText = noStreams
    ? 'This twin has no streams yet.'
    : options.length === 0
      ? 'Every stream is on the plan. Unlink one to free it.'
      : 'No stream matches.';

  return (
    <Section
      onKeyDown={e => {
        // Escape with the list closed leaves a Change and returns to the row — and must not also
        // reach the docked card, which would close the panel (it skips a handled key).
        if (e.key === 'Escape' && changing && !e.defaultPrevented) {
          e.preventDefault();
          pendingFocus.current = 'change';
          setChangingFor(null);
        }
      }}
      onBlur={e => {
        // Leaving a Change with nothing picked puts the row back (spec R1). Focus has gone where the
        // operator sent it, so it is not pulled back to Change. The list is inside this section.
        if (changing && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setChangingFor(null);
        }
      }}
    >
      <span className="label" id={`${pickerId}-label`}>
        Stream
      </span>
      <Combobox
        id={pickerId}
        aria-label={`Stream for ${displayName}`}
        options={options}
        value=""
        onChange={value => {
          if (value == null) return;
          // The link changes the stream, which ends the Change in the render that draws the row.
          pendingFocus.current = 'change';
          onLink(refid, Number(value));
        }}
        // Every unplaced stream: the twin's own streams, a set the wall already draws in full, so
        // the list is bounded by the twin and scrolls rather than hiding the rest behind a cap.
        maxResults={Infinity}
        // A pick is the operator's choice; reaching for another control is not.
        commitOnClose={false}
        size="md"
        onGlass
        disabled={loading}
        placeholder={loading ? 'Loading streams…' : 'Choose a stream'}
        emptyText={emptyText}
      />
      {/* A drop works whenever the wall has tiles: shown while the read loads, hidden only when
          there is nothing to drop (every stream held, or none at all). */}
      {loading || options.length > 0 ? (
        <Hint>Or drop a stream from the wall onto this camera.</Hint>
      ) : null}
    </Section>
  );
};

export default CameraStreamSection;

/* Frames 06–07's Stream section, below a hairline (T:1616–1641). */
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  padding-top: ${space[3]};
  border-top: 1px solid ${semantic.border.default};

  .label {
    font-size: ${typeRole.caption1Strong.fontSize};
    line-height: ${typeRole.caption1Strong.lineHeight};
    font-weight: ${typeRole.caption1Strong.fontWeight};
    color: ${semantic.text.secondary};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[2]};

  .value {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    font-size: ${typeRole.body1Strong.fontSize};
    line-height: ${typeRole.body1Strong.lineHeight};
    font-weight: ${typeRole.body1Strong.fontWeight};
    color: ${semantic.text.primary};
  }
`;

const Hint = styled.p`
  margin: 0;
  font-size: ${typeRole.caption1.fontSize};
  line-height: ${typeRole.caption1.lineHeight};
  color: ${semantic.text.body};
`;
